import { useState, useEffect } from "react";
import { collection, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { Search, Save, Eye, Globe, Share2, FileText, Image as ImageIcon, AlertCircle, CheckCircle, Loader2 } from "lucide-react";

interface SEOPage {
  id: string;
  name: string;
  path: string;
  title: string;
  description: string;
  keywords: string;
  ogImage?: string;
  enabled: boolean;
}

const DEFAULT_PAGES: SEOPage[] = [
  {
    id: "home",
    name: "หน้าแรก",
    path: "/",
    title: "EduMi - แพลตฟอร์มสอนออนไลน์ ขายคอร์สออนไลน์",
    description: "สร้างและขายคอร์สออนไลน์ของคุณเอง ไม่มีค่าธรรมเนียม รองรับนักเรียนไม่จำกัด พร้อม Dashboard และ Analytics",
    keywords: "แพลตฟอร์มสอนออนไลน์, ขายคอร์สออนไลน์, สร้างคอร์สออนไลน์, LMS Thailand, EduMi",
    enabled: true,
  },
  {
    id: "pricing",
    name: "แพ็กเกจ",
    path: "/pricing",
    title: "แพ็กเกจ Pro+ รายเดือน รายปี - SEO Optimization & Sale Page Builder",
    description: "แพ็กเกจ Pro+ สำหรับผู้สอนมืออาชีพ เริ่มต้น 1,590฿/เดือน รองรับนักเรียนไม่จำกัด พื้นที่ 30GB พร้อม SEO Optimization",
    keywords: "แพ็กเกจ Pro+, Pro+ รายเดือน, Pro+ รายปี, SEO Optimization, Sale Page Builder",
    enabled: true,
  },
  {
    id: "faq",
    name: "คำถามที่พบบ่อย",
    path: "/faq",
    title: "คำถามที่พบบ่อย - EduMi",
    description: "คำตอบสำหรับคำถามที่พบบ่อยเกี่ยวกับการใช้งาน EduMi แพลตฟอร์มสอนออนไลน์",
    keywords: "FAQ, คำถามที่พบบ่อย, EduMi, วิธีใช้งาน",
    enabled: true,
  },
];

export default function SEOManagement() {
  const [pages, setPages] = useState<SEOPage[]>(DEFAULT_PAGES);
  const [selectedPage, setSelectedPage] = useState<SEOPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [previewMode, setPreviewMode] = useState<'google' | 'facebook' | 'twitter'>('google');

  useEffect(() => {
    loadSEOSettings();
  }, []);

  const loadSEOSettings = async () => {
    try {
      const docRef = doc(db, "siteSettings", "seo");
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.pages) {
          setPages(data.pages);
          setSelectedPage(data.pages[0]);
        } else {
          setSelectedPage(DEFAULT_PAGES[0]);
        }
      } else {
        setSelectedPage(DEFAULT_PAGES[0]);
      }
    } catch (error) {
      console.error("Error loading SEO settings:", error);
      setSelectedPage(DEFAULT_PAGES[0]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedPage) return;

    setSaving(true);
    setSuccess(false);

    try {
      const updatedPages = pages.map(p => 
        p.id === selectedPage.id ? selectedPage : p
      );

      await setDoc(doc(db, "siteSettings", "seo"), {
        pages: updatedPages,
        updatedAt: serverTimestamp(),
      });

      setPages(updatedPages);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error("Error saving SEO settings:", error);
      alert("เกิดข้อผิดพลาดในการบันทึก");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof SEOPage, value: string | boolean) => {
    if (!selectedPage) return;
    setSelectedPage({ ...selectedPage, [field]: value });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-pink-600 rounded-xl flex items-center justify-center">
              <Search className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">SEO Management</h1>
              <p className="text-gray-600">จัดการ SEO ของเว็บไซต์ให้ติด Google Search</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Page List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                หน้าเว็บไซต์
              </h2>
              <div className="space-y-2">
                {pages.map((page) => (
                  <button
                    key={page.id}
                    onClick={() => setSelectedPage(page)}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                      selectedPage?.id === page.id
                        ? "bg-gradient-to-r from-blue-600 to-pink-600 text-white"
                        : "bg-gray-50 hover:bg-gray-100 text-gray-700"
                    }`}
                  >
                    <div className="font-semibold">{page.name}</div>
                    <div className={`text-sm ${selectedPage?.id === page.id ? 'text-blue-100' : 'text-gray-500'}`}>
                      {page.path}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* SEO Form */}
          <div className="lg:col-span-2 space-y-6">
            {selectedPage && (
              <>
                {/* Form */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900">
                      แก้ไข SEO: {selectedPage.name}
                    </h2>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedPage.enabled}
                        onChange={(e) => updateField('enabled', e.target.checked)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <span className="text-sm text-gray-700">เปิดใช้งาน</span>
                    </label>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Title Tag
                      </label>
                      <input
                        type="text"
                        value={selectedPage.title}
                        onChange={(e) => updateField('title', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                        placeholder="หัวข้อที่จะแสดงใน Google Search"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {selectedPage.title.length}/60 ตัวอักษร (แนะนำ 50-60)
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Meta Description
                      </label>
                      <textarea
                        value={selectedPage.description}
                        onChange={(e) => updateField('description', e.target.value)}
                        rows={3}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                        placeholder="คำอธิบายที่จะแสดงใน Google Search"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {selectedPage.description.length}/160 ตัวอักษร (แนะนำ 150-160)
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Keywords
                      </label>
                      <input
                        type="text"
                        value={selectedPage.keywords}
                        onChange={(e) => updateField('keywords', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                        placeholder="คำค้นหา, แยกด้วยเครื่องหมายจุลภาค"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        แยกคำค้นหาด้วยเครื่องหมายจุลภาค (,)
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <ImageIcon className="w-4 h-4 inline mr-1" />
                        Open Graph Image URL
                      </label>
                      <input
                        type="text"
                        value={selectedPage.ogImage || ''}
                        onChange={(e) => updateField('ogImage', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                        placeholder="https://example.com/image.jpg"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        รูปภาพที่จะแสดงเมื่อแชร์บน Social Media (แนะนำ 1200x630px)
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-pink-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          กำลังบันทึก...
                        </>
                      ) : success ? (
                        <>
                          <CheckCircle className="w-5 h-5" />
                          บันทึกสำเร็จ!
                        </>
                      ) : (
                        <>
                          <Save className="w-5 h-5" />
                          บันทึกการเปลี่ยนแปลง
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Preview */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      <Eye className="w-5 h-5 text-blue-600" />
                      ตัวอย่างการแสดงผล
                    </h3>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setPreviewMode('google')}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          previewMode === 'google'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <Globe className="w-4 h-4 inline mr-1" />
                        Google
                      </button>
                      <button
                        onClick={() => setPreviewMode('facebook')}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          previewMode === 'facebook'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <Share2 className="w-4 h-4 inline mr-1" />
                        Facebook
                      </button>
                    </div>
                  </div>

                  {previewMode === 'google' && (
                    <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="text-xs text-gray-500 mb-1">
                        https://edumi.app{selectedPage.path}
                      </div>
                      <div className="text-xl text-blue-600 hover:underline cursor-pointer mb-1">
                        {selectedPage.title}
                      </div>
                      <div className="text-sm text-gray-700">
                        {selectedPage.description}
                      </div>
                    </div>
                  )}

                  {previewMode === 'facebook' && (
                    <div className="border-2 border-gray-200 rounded-lg overflow-hidden bg-white">
                      {selectedPage.ogImage && (
                        <div className="w-full h-64 bg-gray-200 flex items-center justify-center">
                          <img
                            src={selectedPage.ogImage}
                            alt="OG Preview"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      <div className="p-4">
                        <div className="text-xs text-gray-500 uppercase mb-1">
                          EDUMI.APP
                        </div>
                        <div className="font-bold text-gray-900 mb-1">
                          {selectedPage.title}
                        </div>
                        <div className="text-sm text-gray-600">
                          {selectedPage.description.substring(0, 100)}...
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Tips */}
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-900">
                      <p className="font-semibold mb-2">เคล็ดลับ SEO:</p>
                      <ul className="space-y-1 list-disc list-inside">
                        <li>Title ควรมี 50-60 ตัวอักษร และมีคำค้นหาสำคัญ</li>
                        <li>Description ควรมี 150-160 ตัวอักษร อธิบายชัดเจน</li>
                        <li>Keywords ควรเลือกคำที่เกี่ยวข้องและมีคนค้นหา</li>
                        <li>OG Image ควรมีขนาด 1200x630px สำหรับ Social Media</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
